import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
// import WoodModal from './WoodModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

import { WoodType } from "../../../../types/WoodType";
import { Wood } from "../../../../types/Wood";


import AddPO from './add-po';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const PurchaseOrder: React.FC = () => {
  const [woods, setWidths] = useState<Wood[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Wood | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [woodtype, setWoodType] = useState<any>({});
  const [woodtypedata, setWoodTypeData] = useState<WoodType[]>([]);


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchWood = async (page = 1, limit = 10) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        wood_type: woodtype,
      });

      const data = await apiRequest<Wood[]>({
        method: 'get',
        url: `woods?${params.toString()}`,
      });
      setWidths(data);
    } catch (error) {
      showError('Failed to fetch Wood');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchWood();
  }, []);

  const handleEditClick = (record: Wood) => {
    setIsEditMode(true);
    setInitialValues({
      wood_id: record.wood_id,
      wood_name: record.wood_name,
      wood_thickness: record.wood_thickness,
      wood_length: record.wood_length,
      wood_width: record.wood_width,
      wood_type: record.wood_type,
      wood_type_name: record.wood_type_name,
      wood_status: record.wood_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchWood();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (wood_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-wood/${wood_id}`,
      });
      showSuccess('Wood deleted');
      fetchWood();
    } catch {
      showError('Failed to delete Wood');
    }
  };

  const handleStatusToggle = async (widthId: number) => {
    setWidths((prev) =>
      prev.map((item) =>
        item.wood_id === widthId
          ? { ...item, wood_status: item.wood_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-wood/${widthId}`,
      });
      showSuccess('Wood status updated');
    } catch (error) {
      showError('Failed to update status');
      setWidths((prev) =>
        prev.map((item) =>
          item.wood_id === widthId
            ? { ...item, wood_status: item.wood_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Wood>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };





  const columns: TableColumnsType<Wood> = [
    {
      title: 'Type',
      dataIndex: 'wood_type_name',
      key: 'wood_type_name',
      render: (_, record) => <div>{record.wood_type_name}</div>,
    },
    {
      title: 'Wood',
      dataIndex: 'wood_name',
      key: 'wood_name',
      filters: Array.from(new Set(woods.map((s) => s.wood_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.wood_name || null,
      onFilter: (value, record) => record.wood_name.includes(value as string),
      sorter: (a, b) => a.wood_name.localeCompare(b.wood_name),
      sortOrder: sortedInfo.columnKey === 'wood_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.wood_name}</div>,
    },
    {
      title: 'Thickness',
      dataIndex: 'wood_thickness',
      key: 'wood_thickness',
      render: (_, record) => <div>{record.wood_thickness}</div>,
    },
    {
      title: 'Length',
      dataIndex: 'wood_length',
      key: 'wood_length',
      render: (_, record) => <div>{record.wood_length}</div>,
    },
    {
      title: 'Width',
      dataIndex: 'wood_width',
      key: 'wood_width',
      render: (_, record) => <div>{record.wood_width}</div>,
    },
  ];



  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Wood) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.wood_status}
                onChange={() => handleStatusToggle(record.wood_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Wood?"
              onConfirm={() => handleDelete(record.wood_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  const fetchWoodTypes = async () => {
    try {
      const data = await apiRequest<WoodType[]>({
        method: 'get',
        url: 'wood-types?status=1',
      });
      setWoodTypeData(data);
    } catch (error) {
      showError('Failed to fetch wood type');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchWoodTypes()
  }, []);
  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Wood</Breadcrumb.Item>
        </Breadcrumb>
        <Flex gap="small">
          <Flex gap="middle" align="center" style={{ marginBottom: 0, display: 'flex' }} wrap>
            <Select
              onChange={(value) => setWoodType(value)}
              placeholder="Select wood type"
              options={woodtypedata.map(wt => ({ label: wt.wood_name, value: wt.wood_id }))}
            />
          </Flex>
          <Flex gap="small">
            <Button style={{ display: 'flex', alignSelf: 'center' }} type="primary" onClick={() => fetchWood(1, pagination.pageSize)}>
              Search
            </Button>
          </Flex>
          {currentModule?.right_add === 'Y' && (
            <Flex gap="small" align="center">
              <Button type="primary" onClick={showCreateModal}>
                Add Purchase Order
              </Button>
            </Flex>
          )}
        </Flex>



      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Wood>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="wood_id"
          dataSource={woods.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 14 }}
          locale={{ emptyText: 'No wood found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, woods.length)} of{' '}
                {woods.length}
              </span>
              <Pagination
                {...pagination}
                total={woods.length}
                showSizeChanger
                onChange={(page, size) => fetchWood(page, size)}
              />
            </Flex>
          )}
        />
      </div>

      <AddPO
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        woodTypesData={woodtypedata}
      />
    </>
  );
};

export default PurchaseOrder;